// src/stores/CustomerStore.js - Zustand Store replacing CustomerSocketService + CustomersProvider
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Store creation with selector subscription middleware for advanced features
const useCustomerStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE (mirrors CustomersProvider exactly)
    // ================================
    
    // Customer list state
    customers: [],
    customersLoading: false,
    customerSearchResults: [],
    customerSearchLoading: false,
    customersPagination: null,

    // Selected customer state (for CustomerDetails page)
    selectedCustomer: null,
    selectedCustomerLoading: false,
    
    // Granular saving flags (canonical pattern)
    creatingCustomer: false,
    savingCustomer: false,
    deletingCustomer: false,

    // Connection state
    isConnected: false,
    
    // Cache state (from CustomerSocketService)
    cache: {
      customers: [],
      pagination: null,
      selectedCustomer: null,
      searchResults: [],
      lastFetch: null
    },
    
    // ================================
    // SOCKET OPERATIONS (from CustomerSocketService)
    // ================================
    
    /**
     * Request paginated list of customers - NO RETURN VALUE
     * @param {object} options - Pagination and filtering options
     */
    loadCustomers: (options = {}) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ CustomerStore: Socket service not initialized');
        return;
      }

      const {
        page = 1,
        limit = 50,
        search = '',
        sortBy = 'created_at',
        sortOrder = 'desc'
      } = options;

      console.log('📋 CustomerStore: Loading customers with options:', { page, limit, search });

      set({ customersLoading: true });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_CUSTOMERS, {
        page,
        limit,
        search,
        sort_by: sortBy,
        sort_order: sortOrder
      });
    },

    /**
     * Request customer search - NO RETURN VALUE
     * @param {string} searchTerm - Search query
     * @param {number} limit - Max results
     */
    searchCustomers: (searchTerm, limit = 10) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ CustomerStore: Socket service not initialized');
        return;
      }

      console.log('🔍 CustomerStore: Searching customers:', searchTerm);

      if (!searchTerm || searchTerm.length < 2) {
        set({ customerSearchResults: [] });
        return;
      }

      set({ customerSearchLoading: true });

      socketManager.universalHandler.emit(SOCKET_EVENTS.SEARCH_CUSTOMERS, {
        search: searchTerm,
        limit
      });
    },

    /**
     * Request detailed customer information - NO RETURN VALUE
     * @param {string} customerId - Customer ID
     * @param {object} options - What data to include
     */
    loadCustomerDetails: (customerId, options = {}) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ CustomerStore: Socket service not initialized');
        return;
      }

      const {
        includeAppointments = true,
        includeInvoices = true,
        includeStatistics = true
      } = options;

      console.log('👤 CustomerStore: Loading customer details:', customerId);

      set({ selectedCustomerLoading: true });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_CUSTOMER_DETAILS, {
        customer_id: customerId,
        include_appointments: includeAppointments,
        include_invoices: includeInvoices,
        include_statistics: includeStatistics
      });
    },

    /**
     * Request to create a new customer - NO RETURN VALUE
     * @param {object} customerData - Customer information
     */
    createCustomer: (customerData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ CustomerStore: Socket service not initialized');
        return;
      }

      console.log('➕ CustomerStore: Creating customer:', customerData.name);

      set({ creatingCustomer: true });
      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_CUSTOMER, customerData);
    },

    /**
     * Request to update an existing customer - NO RETURN VALUE
     * @param {string} customerId - Customer ID
     * @param {object} updateData - Fields to update
     */
    updateCustomer: (customerId, updateData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ CustomerStore: Socket service not initialized');
        return;
      }

      console.log('✏️ CustomerStore: Updating customer:', customerId);

      set({ savingCustomer: true });
      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_CUSTOMER, {
        customer_id: customerId,
        ...updateData
      });
    },

    /**
     * Request to delete a customer - NO RETURN VALUE
     * @param {string} customerId - Customer ID
     */
    deleteCustomer: (customerId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ CustomerStore: Socket service not initialized');
        return;
      }

      console.log('🗑️ CustomerStore: Deleting customer:', customerId);

      set({ deletingCustomer: true });
      socketManager.universalHandler.emit(SOCKET_EVENTS.DELETE_CUSTOMER, {
        customer_id: customerId
      });
    },
    
    // ================================
    // UTILITY FUNCTIONS (from CustomersProvider)
    // ================================

    clearSelectedCustomer: () => {
      set({ selectedCustomer: null });
    },
    
    refreshCustomers: (options = {}) => {
      // Clear cache first
      set(state => ({
        cache: {
          customers: [],
          pagination: null,
          selectedCustomer: null,
          searchResults: [],
          lastFetch: null
        }
      }));
      
      // Then reload with force option
      get().loadCustomers({ ...options, force: true });
    },
    
    getCustomerById: (customerId) => {
      const { customers } = get();
      return customers.find(c => c.id === customerId) || null;
    },
    
    // ================================
    // INTERNAL STATE UPDATERS (called by socket listeners)
    // ================================
    
    _setCustomersLoaded: (data) => {
      console.log('📡 CustomerStore: Customers list received');

      set(state => ({
        customers: data.customers || [],
        customersPagination: data.pagination,
        customersLoading: false,
        cache: {
          ...state.cache,
          customers: data.customers || [],
          pagination: data.pagination,
          lastFetch: new Date()
        }
      }));
    },

    _setCustomerSearchResults: (data) => {
      console.log('📡 CustomerStore: Customer search results received');

      set(state => ({
        customerSearchResults: data || [],
        customerSearchLoading: false,
        cache: {
          ...state.cache,
          searchResults: data || []
        }
      }));
    },

    _setCustomerDetailsLoaded: (data) => {
      console.log('📡 CustomerStore: Customer details received:', data?.name);

      // Backend now returns flat structure: { id: '...', name: '...', appointments: [...], invoices: [...] }
      console.log('📊 CustomerStore: Appointments count:', data?.appointments?.length || 0);
      console.log('📊 CustomerStore: Invoices count:', data?.invoices?.length || 0);

      set(state => ({
        selectedCustomer: {
          customer: data,
          appointments: data?.appointments || [],
          invoices: data?.invoices || []
        },
        selectedCustomerLoading: false,
        cache: {
          ...state.cache,
          selectedCustomer: data
        }
      }));
    },
    
    _addCustomer: (customerData) => {
      console.log('📡 CustomerStore: New customer created:', customerData.name);

      set(state => {
        // Check if customer already exists to prevent duplicates
        const exists = state.customers.find(c => c.id === customerData.id);
        if (exists) {
          console.log('⚠️ CustomerStore: Customer already exists, skipping duplicate');
          return state;
        }

        const newCustomers = [customerData, ...state.customers];

        return {
          customers: newCustomers,
          cache: {
            ...state.cache,
            customers: newCustomers
          }
        };
      });
    },

    _updateCustomer: (customerData) => {
      console.log('📡 CustomerStore: Customer updated:', customerData.name);

      set(state => {
        // Update in customers list
        const updatedCustomers = state.customers.map(customer =>
          customer.id === customerData.id
            ? { ...customer, ...customerData }
            : customer
        );

        // Update selected customer if it's the same one
        let updatedSelectedCustomer = state.selectedCustomer;
        if (state.selectedCustomer?.customer?.id === customerData.id) {
          updatedSelectedCustomer = {
            ...state.selectedCustomer,
            customer: { ...state.selectedCustomer.customer, ...customerData }
          };
        }

        return {
          customers: updatedCustomers,
          selectedCustomer: updatedSelectedCustomer,
          cache: {
            ...state.cache,
            customers: updatedCustomers,
            selectedCustomer: updatedSelectedCustomer
          }
        };
      });
    },

    _removeCustomer: (customerId) => {
      console.log('📡 CustomerStore: Customer deleted:', customerId);

      set(state => {
        const filteredCustomers = state.customers.filter(c => c.id !== customerId);

        // Clear selected customer if it was deleted
        const updatedSelectedCustomer = state.selectedCustomer?.customer?.id === customerId
          ? null
          : state.selectedCustomer;

        return {
          customers: filteredCustomers,
          selectedCustomer: updatedSelectedCustomer,
          cache: {
            ...state.cache,
            customers: filteredCustomers,
            selectedCustomer: updatedSelectedCustomer
          }
        };
      });
    },

    // ================================
    // SOCKET CONNECTION MANAGEMENT
    // ================================
    
    _setConnectionState: (isConnected) => {
      set({ isConnected });
    },
    
    _getSocketManager: () => {
      // This will be set by the socket manager initialization
      // For now, we'll access it from window if available
      if (typeof window !== 'undefined' && window.__socketManager) {
        return { socketManager: window.__socketManager };
      }
      return { socketManager: null };
    },
    
    _handleDisconnect: () => {
      set({
        creatingCustomer: false,
        savingCustomer: false,
        deletingCustomer: false,
        customersLoading: false,
        customerSearchLoading: false,
        selectedCustomerLoading: false,
      });
    },

    _handleAuthReset: () => {
      console.log('🧹 CustomerStore: Resetting all state due to auth reset');

      set({
        // Reset all state to initial values
        customers: [],
        customersLoading: false,
        customersPagination: null,
        customerSearchResults: [],
        customerSearchLoading: false,
        selectedCustomer: null,
        selectedCustomerLoading: false,
        creatingCustomer: false,
        savingCustomer: false,
        deletingCustomer: false,
        isConnected: false,
        cache: {
          customers: [],
          pagination: null,
          selectedCustomer: null,
          searchResults: [],
          lastFetch: null
        },
        _initialized: false
      });
    },
    
    // ================================
    // INITIALIZATION AND AUTO-LOAD
    // ================================
    
    _initialized: false,
    
    _initialize: (socketManager) => {
      const state = get();
      
      // REMOVED: The check that was preventing re-initialization
      // We want to reinitialize after logout/login
      
      console.log('🏪 CustomerStore: Initializing with socket manager');
      
      // Store socket manager reference
      if (typeof window !== 'undefined') {
        window.__socketManager = socketManager;
      }
      
      set({ 
        _initialized: true,
        isConnected: socketManager?.isAuthenticated || false 
      });
      
      // Auto-load customers if authenticated
      if (socketManager?.isAuthenticated) {
        console.log('🔄 CustomerStore: Auto-loading customers (first time)');
        state.loadCustomers();
      }
    }
  }))
);

// ================================
// SOCKET EVENT LISTENER SETUP (runs once when module loads)
// ================================

// REMOVED: The module-level flag that was causing the re-login bug

export const initializeCustomerStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    return;
  }
  
  console.log('👂 CustomerStore: Setting up socket event listeners');
  
  const { universalHandler } = socketManager;
  
  // Set up universal response listener for all customer events
  const handleSocketResponse = (responseData) => {
    const eventName = responseData.event;
    const store = useCustomerStore.getState();
    
    switch (eventName) {
      // ✅ SUCCESS EVENTS
      case SOCKET_EVENTS.CUSTOMERS_LOADED:
        store._setCustomersLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.CUSTOMERS_SEARCH_RESULTS:
        store._setCustomerSearchResults(responseData.data);
        break;
        
      case SOCKET_EVENTS.CUSTOMER_DETAILS_LOADED:
        store._setCustomerDetailsLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.CUSTOMER_CREATED:
        useCustomerStore.setState({ creatingCustomer: false });
        // Handle different possible response structures
        const customerData = responseData.data?.customer || responseData.data || responseData;
        store._addCustomer(customerData);
        break;

      case SOCKET_EVENTS.CUSTOMER_UPDATED:
        useCustomerStore.setState({ savingCustomer: false });
        store._updateCustomer(responseData.data);
        break;

      case SOCKET_EVENTS.CUSTOMER_DELETED:
        useCustomerStore.setState({ deletingCustomer: false });
        store._removeCustomer(responseData.data.id);
        break;
        
      // ❌ ERROR EVENTS - NotificationListener handles display
      case SOCKET_EVENTS.GET_CUSTOMERS_FAILED:
        useCustomerStore.setState({ customersLoading: false });
        break;

      case SOCKET_EVENTS.GET_CUSTOMER_DETAILS_FAILED:
        useCustomerStore.setState({ selectedCustomerLoading: false });
        break;

      case SOCKET_EVENTS.SEARCH_CUSTOMERS_FAILED:
        useCustomerStore.setState({ customerSearchLoading: false });
        break;

      case SOCKET_EVENTS.CREATE_CUSTOMER_FAILED:
        useCustomerStore.setState({ creatingCustomer: false });
        console.log('❌ CustomerStore: Create failed -', responseData.message);
        break;

      case SOCKET_EVENTS.UPDATE_CUSTOMER_FAILED:
        useCustomerStore.setState({ savingCustomer: false });
        console.log('❌ CustomerStore: Update failed -', responseData.message);
        break;

      case SOCKET_EVENTS.DELETE_CUSTOMER_FAILED:
        useCustomerStore.setState({ deletingCustomer: false });
        console.log('❌ CustomerStore: Delete failed -', responseData.message);
        break;
        
      default:
        // Not a customer-related event, ignore
        break;
    }
  };
  
  // Listen to the universal response channel (uses existing UniversalResponseHandler)
  const originalOn = universalHandler.on.bind(universalHandler);
  
  // Override the universal handler to catch our customer events
  const customerEvents = [
    SOCKET_EVENTS.CUSTOMERS_LOADED,
    SOCKET_EVENTS.CUSTOMERS_SEARCH_RESULTS,
    SOCKET_EVENTS.CUSTOMER_DETAILS_LOADED,
    SOCKET_EVENTS.CUSTOMER_CREATED,
    SOCKET_EVENTS.CUSTOMER_UPDATED,
    SOCKET_EVENTS.CUSTOMER_DELETED,
    SOCKET_EVENTS.GET_CUSTOMERS_FAILED,
    SOCKET_EVENTS.GET_CUSTOMER_DETAILS_FAILED,
    SOCKET_EVENTS.SEARCH_CUSTOMERS_FAILED,
    SOCKET_EVENTS.CREATE_CUSTOMER_FAILED,
    SOCKET_EVENTS.UPDATE_CUSTOMER_FAILED,
    SOCKET_EVENTS.DELETE_CUSTOMER_FAILED
  ];
  
  // Set up individual event listeners using existing system
  customerEvents.forEach(eventName => {
    universalHandler.on(eventName, (data) => {
      handleSocketResponse({ event: eventName, data });
    });
  });
  
  // Set up auth reset listener and socket-disconnected listener
  if (typeof window !== 'undefined') {
    window.addEventListener('auth-state-reset', () => {
      useCustomerStore.getState()._handleAuthReset();
    });
    window.addEventListener('socket-disconnected', () => {
      useCustomerStore.getState()._handleDisconnect();
    });
  }
  
  // REMOVED: The line that set socketEventListenersSetup = true
  
  // Initialize the store
  useCustomerStore.getState()._initialize(socketManager);
  
  console.log('✅ CustomerStore: Socket event listeners initialized');
};

export default useCustomerStore;